import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button, Form } from 'antd';
import { ModalProps } from 'antd/es/modal';
import { AssetInAssetDashboard, ClientInAssetDashboard, ProjectInAssetDashboard } from 'models/assets.model';
import AssetEditForm from 'components/molecules/AssetEditForm';
import { useAssetType } from 'hooks/useAssetType';
import { useEnvironment } from 'hooks/useEnvironment';
import { useUserSession } from 'hooks/useUserSession';
import { useMessage } from 'hooks/useMessage';
import { ProjectListResponseDataModel } from 'core/domain/project/model/projectModel';
import AssetEditLoadingFormTemplate from './loadingTemplate';
import { GetProjectsListByClientIdWithPagination } from 'core/domain/project/repositories/getProjectsListByClientIdWithPagination';

const FORM_ID = 'form_assetEdit';

const PAGE_SIZE = 1000;

export interface AssetUpdateFormModel {
  clientId: string;
  communityId: string;
  alias?: string;
  assetType: string;
  streetType: string;
  streetName: string;
  blockNumber: string;
  staircase?: string;
  flat?: string;
  door?: string;
  postalCode: string;
  province: string;
  city: string;
  country: string;
  isBlocked?: boolean;
  description: string;
  externalId?: string;
}

interface AssetEditModalProps extends ModalProps {
  asset: AssetInAssetDashboard;
  client: ClientInAssetDashboard;
  project: ProjectInAssetDashboard;
  onSubmit: (values: AssetUpdateFormModel) => Promise<void>;
  updating: boolean;
}

const AssetEditModal: FC<AssetEditModalProps> = ({
  asset,
  client,
  project,
  onCancel,
  onSubmit,
  updating,
  ...props
}) => {
  const { t } = useTranslation();
  const { host } = useEnvironment();
  const { token } = useUserSession();
  const { setMessageError } = useMessage();
  const [isBlocked, setIsBlocked] = useState<boolean>(asset?.is_blocked || false);
  const [form] = Form.useForm<AssetUpdateFormModel>();
  const [projects, setProjects] = useState<ProjectListResponseDataModel[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { data } = useAssetType();

  useEffect(() => {
    form.setFieldsValue({ isBlocked })
  }, [isBlocked, form])

  const setInitialFormValues = useCallback(() => {
    form.setFieldsValue({
      alias: asset?.alias || '',
      streetType: asset?.street_type || '',
      streetName: asset?.street_name || '',
      blockNumber: asset?.block_number || '',
      staircase: asset?.staircase || '',
      flat: asset?.flat || '',
      door: asset?.door || '',
      postalCode: asset?.zip_code || '',
      city: asset?.city || '',
      province: asset?.state || '',
      country: asset?.country || '',
      assetType: asset?.type,
      description: asset?.description || '',
      externalId: asset?.external_id || '',
      communityId: project.id || '',
    });
  }, [asset, project, form]);

  useEffect(() => {
    setInitialFormValues();
  }, [setInitialFormValues]);

  const getProjectsListByClientId = async () => {
    try {
      setIsLoading(true);
      const response = await GetProjectsListByClientIdWithPagination({
        token,
        host,
        clientId: client.id,
        pageNumber: 1,
        pageSize: PAGE_SIZE,
      })
      !!response && setProjects(response.data);
    } catch (error) {
      setMessageError({ description: t('_UPDATE_ASSET_LOADING_DATA_ERROR') });
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    !!client.id && props.open && getProjectsListByClientId()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, props.open]);

  const handleOnFinish = async (values: AssetUpdateFormModel) => {
    await onSubmit(values);
  };

  const handleOnCancel = (event: any) => {
    setInitialFormValues();
    onCancel && onCancel(event);
  };

  const handleOnIsBlocked = (isBlocked: boolean) => setIsBlocked(isBlocked)

  return (
    <Modal
      {...props}
      title={t('edit_asset')}
      onCancel={handleOnCancel}
      footer={[
        <Button key={'back'} onClick={handleOnCancel}>
          {t('to_cancel')}
        </Button>,
        <Button key={'submit'} type={'primary'} loading={updating || isLoading} htmlType={'submit'} form={FORM_ID}>
          {t('edit')}
        </Button>,
      ]}
      destroyOnClose
    >
      <>
        {isLoading && <AssetEditLoadingFormTemplate />}
        {!isLoading && (
          <AssetEditForm
            form={form}
            id={FORM_ID}
            onFinish={handleOnFinish}
            assetTypesList={data}
            isBlocked={isBlocked}
            onBlocked={handleOnIsBlocked}
            projects={projects}
          />
        )}
      </>
    </Modal>
  );
};

export default AssetEditModal;
